import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { format, parseISO } from 'date-fns';

import { FindAssignmentsByGuestAndDateQuery } from '../generated/graphql';
import { DuplicateBookingData } from '../types/duplicateBooking';
import { getHotelName, getHotelLocation } from '../utilities/hotelHelpers';

type Props = {
  duplicateData?: DuplicateBookingData;
  className?: string;
};

type BookingArray = NonNullable<
  FindAssignmentsByGuestAndDateQuery['findAssignmentsByGuestAndDate']
>;
type Booking = BookingArray[number];

const BookingItem = ({ booking }: { booking: Booking }) => (
  <li>
    <span className="font-semibold">
      {format(parseISO(booking.checkInDate), 'M/d/yyyy')}
      {' - '}
      {format(parseISO(booking.checkOutDate), 'M/d/yyyy')}
    </span>
    <span className="ml-2">
      at {getHotelName(booking.hotel)} ({getHotelLocation(booking.hotel)})
    </span>
    {booking.hotelRoomNumber && (
      <span className="ml-1 text-gray-600">
        - Room {booking.hotelRoomNumber}
      </span>
    )}
  </li>
);

export default function DuplicateBookingWarning({
  duplicateData,
  className,
}: Props) {
  if (
    !duplicateData?.hasOverlappingBookings ||
    !duplicateData.overlappingBookings?.length
  ) {
    return null;
  }

  const { overlappingBookings, warningMessage } = duplicateData;

  return (
    <div className={`rounded-md border bg-yellow-50 p-4 ${className || ''}`}>
      <div className="flex self-start">
        <div className="shrink-0">
          <ExclamationCircleIcon className="size-5 text-yellow-500" />
        </div>
        <div className="ml-3">
          <h3 className="mb-2 font-medium text-yellow-700">
            {warningMessage || 'Duplicate booking detected'}
          </h3>
          <ul className="list-inside list-disc text-sm font-medium text-yellow-700">
            {overlappingBookings?.map((booking) => (
              <BookingItem key={booking.id} booking={booking} />
            ))}
          </ul>
          <p className="mt-2 text-sm text-yellow-600">
            You can continue with the booking, but please verify the dates are
            correct.
          </p>
        </div>
      </div>
    </div>
  );
}
